import { assertNever, CustomComponentCustomFormField, CustomForm, CustomFormAnswer, CustomFormField, customFormHelper, CustomFormInputField, CustomFormLayout, CustomFormReportData, CustomFormSegmentReportData, CustomFormSelection, CustomFormSelectionOverview } from "../../../amorg-common";
import { PrettyPrintJson } from "../../../designSystem/compositeComponents/PrettyPrintJson";
import { CustomFormFilter } from "../../../utils/customFormHelper";
import React from "react";
import { H3 } from "../../../designSystem/typography/Heading";
import { TimePointsCustomFormFieldComponent } from "./answerFieldComponents/custom/TimePointsCustomFormFieldComponent";
import { RadioButtonColumnCustomFormFieldComponent } from "./answerFieldComponents/RadioButtonColumnCustomFormFieldComponent";
import { RadioButtonRowCustomFormFieldComponent } from "./answerFieldComponents/RadioButtonRowCustomFormFieldComponent";
import { FileUploadCustomFormFieldComponent } from "./answerFieldComponents/FileUploadCustomFormFieldComponent";
import { FreeTextCustomFormFieldComponent } from "./answerFieldComponents/FreeTextCustomFormFieldComponent";
import { SuggesterCustomFormFieldComponent } from "./answerFieldComponents/SuggesterCustomFormFieldComponent";
import { RiskAssessmentReportField } from "./reportValueComponents/custom/RiskAssessmentReportField";
import { TimePointsReportField } from "./reportValueComponents/custom/TimePointsReportField";
import { DiscreteValueReportField } from "./reportValueComponents/DiscreteValueReportField";
import { FreeTextReportField } from "./reportValueComponents/FreeTextReportField";
import { StaticTextCustomFormFieldComponent } from "./staticFieldComponents/StaticTextCustomFormFieldComponent";
import { FileUploadReportField } from "./reportValueComponents/FileUploadReportField";
import { PrintBreakAfter, PrintBreakBefore } from "../../../designSystem/compositeComponents/Print";
import { MatrixRadioButtonsCustomFormFieldComponent } from "./answerFieldComponents/MatrixRadioButtonsCustomFormFieldComponent";
import { Kim1RiskAssessmentCustomFormFieldComponent } from "./answerFieldComponents/custom/Kim1RiskAssessmentCustomFormFieldComponent";
import { Kim2RiskAssessmentCustomFormFieldComponent } from "./answerFieldComponents/custom/Kim2RiskAssessmentCustomFormFieldComponent";
import { Kim3RiskAssessmentCustomFormFieldComponent } from "./answerFieldComponents/custom/Kim3RiskAssessmentCustomFormFieldComponent";
import { StaticTextImportantCustomFormFieldComponent } from "./staticFieldComponents/StaticTextImportantCustomFormFieldComponent";

export const customFormFieldFactory = {
    getFieldValues,
    getFilterValues,
    getAnswerFieldComponent,
    getAnswerFieldText,
    getReportFieldValueComponent
};

function getFilterValues(field:CustomFormInputField, report:CustomFormReportData):CustomFormFilter | undefined
{
    if(field.isSegmentation){
        const type = field.type;
        if(type === 'RadioButtonColumn' || type === 'RadioButtonRow' || type === 'MatrixRadioButtons')
        {
            return {
                fieldId:field.fieldId, 
                label:field.label, 
                selectedValue:'', 
                values:getFieldValues(field, report)
            };
        }
        else if(type === 'Suggester' || type === 'FreeText')
        {
            return {
                fieldId:field.fieldId, 
                label:field.label, 
                selectedValue:'', 
                values:getFieldValues(field, report)
            };
        }
        else if(type === 'FileUpload' || type === 'CustomComponent' || type === 'HtmlMarkup')
        {
            return undefined;
        }
        else
            assertNever(type);
    }
    return undefined;
}

function getFieldValues(field:CustomFormInputField, report:CustomFormReportData):{val:string, label:string}[]
{
    return customFormHelper.getFieldValues(field, report);
}

export type AnswerFieldContext = {
    siteId:string,
    disabled:boolean,
    form:CustomForm,
    answer:CustomFormAnswer,
    videoSubmissionEnabled:boolean,
    inPrint?:boolean,
    doUpdate:()=>Promise<void>,
    onSubmit:(selections:CustomFormSelection[])=>Promise<void>,
    getFieldOptions:()=>Promise<CustomFormSelectionOverview>
}

function getAnswerFieldComponent(field:CustomFormField, ctx:AnswerFieldContext):JSX.Element
{
    const type = field.type;
    switch(type)
    {
        case 'RadioButtonColumn':
            return <RadioButtonColumnCustomFormFieldComponent field={field} {...ctx} />;

        case 'RadioButtonRow':
            return <RadioButtonRowCustomFormFieldComponent field={field} {...ctx} />;
        
        case 'MatrixRadioButtons':
            return <MatrixRadioButtonsCustomFormFieldComponent field={field} {...ctx} />;

        case 'Suggester':
            return <SuggesterCustomFormFieldComponent field={field} {...ctx} />;

        case 'FreeText':
            return <FreeTextCustomFormFieldComponent field={field} {...ctx} />;

        case 'FileUpload':
                return ctx.videoSubmissionEnabled 
                    ? <FileUploadCustomFormFieldComponent field={field} {...ctx} />
                    : null;

        case 'CustomComponent':
                return getCustomComponent(field, ctx);

        case 'StaticText':
                return <StaticTextCustomFormFieldComponent field={field} />;
        
        case 'StaticTextImportant':
            return <StaticTextImportantCustomFormFieldComponent field={field} />;
        
        case 'HtmlLayout':
            return getMarkupComponent(field);
            
        
        default: assertNever(type);
    }

    function getCustomComponent(field:CustomComponentCustomFormField, ctx:AnswerFieldContext):JSX.Element
    {
        switch(field.componentName)
        {
            case 'TimePoints':
                return <TimePointsCustomFormFieldComponent field={field} {...ctx} />

            case 'RiskAssessmentKim1':
                return <Kim1RiskAssessmentCustomFormFieldComponent field={field} {...ctx} />;
    
            case 'RiskAssessmentKim2':
                return <Kim2RiskAssessmentCustomFormFieldComponent field={field} {...ctx} />;
    
            case 'RiskAssessmentKim3':
                return <Kim3RiskAssessmentCustomFormFieldComponent field={field} {...ctx} />;
    

            default:
                return DefaultPlaceHolderComponent(field);
        }
    }


    function getMarkupComponent(field:CustomFormLayout):JSX.Element
    {
        switch(field.componentName)
        {
            case 'PrintBreakBefore':
                return <PrintBreakBefore />;
            case 'PrintBreakAfter':
                    return <PrintBreakAfter />;
    
            default:
                return <>Empty</>;
        }
    }


    function DefaultPlaceHolderComponent(field:CustomFormField):JSX.Element
    {
        return (
            <section key={field.fieldId}>
            <H3>{field.fieldId}</H3>

            <PrettyPrintJson data={field} />
        </section> 
        );
    }
}

function getAnswerFieldText(field:CustomFormInputField, selection:CustomFormSelection):string
{
    const noValuePlaceholder = '*ingen værdi*';
    const type = field.type;
    switch(type)
    {
        case 'FreeText': return selection.value || noValuePlaceholder;
        case 'Suggester': return selection.value || noValuePlaceholder;
        case 'RadioButtonColumn': return field.options.find(o => o.value===selection.value)?.reportText  || noValuePlaceholder;
        case 'RadioButtonRow': return field.options.find(o => o.value===selection.value)?.reportText  || noValuePlaceholder;
        case 'MatrixRadioButtons': return field.sections.flatMap(s => s.rows.flatMap(r => r?.options)).find(o => o?.value===selection.value)?.reportText  || noValuePlaceholder;
        case 'FileUpload': return selection.value || noValuePlaceholder;
        case 'CustomComponent': return selection.value || noValuePlaceholder;
        default: assertNever(type);
    }
    return selection?.value || '*ingen værdi*';
}

function getReportFieldValueComponent(field:CustomFormInputField, segmentReports:CustomFormSegmentReportData[]):JSX.Element
{
    switch(field.type)
    {
        case 'RadioButtonColumn':
            return <DiscreteValueReportField key={field.fieldId}  fieldId={field.fieldId} fieldLabel={field.label} 
                valueTexts={field.options.map(o => ({ value:o.value, text:o.reportText }))}
                segmentReports={segmentReports} />;
        
        case 'RadioButtonRow':
            return <DiscreteValueReportField key={field.fieldId}  fieldId={field.fieldId} fieldLabel={field.label}
                valueTexts={field.options.map(o => ({ value:o.value, text:o.reportText }))}
                segmentReports={segmentReports} />;
        
        case 'MatrixRadioButtons':
            return <DiscreteValueReportField key={field.fieldId}  fieldId={field.fieldId} fieldLabel={field.label}
                valueTexts={field.sections.flatMap(s => s.rows.flatMap(r => r?.options)).filter(o => o !== null && o !== undefined).map(o => ({ value:o.value, text:o.reportText }))}
                segmentReports={segmentReports} />;
                
        case 'Suggester':
            return <DiscreteValueReportField key={field.fieldId}  fieldId={field.fieldId} fieldLabel={field.label}
                segmentReports={segmentReports} />;

        case 'FreeText':
            return <FreeTextReportField key={field.fieldId} fieldId={field.fieldId} fieldLabel={field.label}
                segmentReports={segmentReports} />;
        
        case 'FileUpload':
                return <FileUploadReportField key={field.fieldId} fieldId={field.fieldId} fieldLabel={field.label} segmentReports={segmentReports} />;

        case 'CustomComponent':
                return getCustomComponent(field, segmentReports);
        
        default: assertNever(field);
    }
    
    function getCustomComponent(field:CustomComponentCustomFormField, segmentReports:CustomFormSegmentReportData[])
    {
        switch(field.componentName)
        {
            case 'TimePoints':
                return <TimePointsReportField key={field.fieldId} field={field} segmentReports={segmentReports} />;

            case 'RiskAssessmentKim1':
                return <RiskAssessmentReportField key={field.fieldId} field={field} segmentReports={segmentReports} />;

            case 'RiskAssessmentKim2':
                return <RiskAssessmentReportField key={field.fieldId} field={field} segmentReports={segmentReports} />;

            case 'RiskAssessmentKim3':
                return <RiskAssessmentReportField key={field.fieldId} field={field} segmentReports={segmentReports} />;

            default:
                return DefaultPlaceHolderComponent(field);
        }
    }

    function DefaultPlaceHolderComponent(field:CustomFormField):JSX.Element
    {
        return (
            <section key={field.fieldId}>
            <H3>{field.fieldId}</H3>

            <PrettyPrintJson data={field} />
        </section> 
        );
    }
}
